import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Link,
  TextField,
} from '@material-ui/core';

const Field = styled.div`
  margin-bottom: 14px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  height: 44px;
  border: none;
  background: #111827;
  color: #ffffff;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.2px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #1f2937;
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
`;

const Note = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 8px;
`;

const apiBaseUrl = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/$/, '')
  : process.env.NODE_ENV === 'development'
  ? 'http://localhost:3008'
  : 'https://yielding-linnell-ev-payment-a515489f.koyeb.app';

const NowPaymentsCheckout = ({
  price,
  description = 'Payment',
  basePrice,
  tax = 0,
  autoCheckTerms = false,
  currency = 'eur',
  metadata: extraMetadata,
  prefillEmail = '',
  prefillName = '',
  hideTaxBreakdown = false,
  successCtaUrl,
  successCtaLabel,
}) => {
  const currencySymbol = currency === 'usd' ? '$' : '€';

  const [billingDetails, setBillingDetails] = useState({
    name: prefillName || '',
    email: prefillEmail || '',
  });
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  useEffect(() => {
    if (autoCheckTerms) setAcceptedTerms(true);
  }, [autoCheckTerms, description]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      setError('You must accept the Terms & Conditions');
      return;
    }
    if (billingDetails.name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(billingDetails.email)) {
      setError('Invalid email format');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const res = await fetch(`${apiBaseUrl}/api/nowpayments/create-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Math.round(price * 100),
          currency: currency,
          billingDetails,
          metadata: {
            service_description: description,
            terms_accepted: true,
            ...(extraMetadata || {}),
          },
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create invoice');

      // Redirect to NOWPayments hosted invoice page
      if (data.invoice_url) {
        window.location.href = data.invoice_url;
      } else {
        throw new Error('No invoice URL returned');
      }
    } catch (err) {
      setError(err.message || 'Payment failed. Try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Dialog open={termsOpen} onClose={() => setTermsOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>Terms &amp; Conditions</DialogTitle>
        <DialogContent dividers>
          <Typography variant='h6' gutterBottom>Scope of Service</Typography>
          <Typography paragraph>
            You are purchasing a digital subscription service. Payments are processed through NOWPayments
            and automatically converted to cryptocurrency.
          </Typography>
          <Typography variant='h6' gutterBottom>Payment Processing</Typography>
          <Typography paragraph>
            Card payments are securely handled by NOWPayments. Fiat currency is automatically converted to
            cryptocurrency. No crypto knowledge is required from the user.
          </Typography>
          <Typography variant='h6' gutterBottom>Refunds</Typography>
          <Typography paragraph>
            Due to the nature of cryptocurrency transactions, refunds are handled on a case-by-case basis.
            Contact support for any refund requests.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermsOpen(false)} color='primary'>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={privacyOpen} onClose={() => setPrivacyOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Privacy Policy</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>
            We collect your name and email to process your payment and deliver your subscription.
            Payment details are handled by NOWPayments — we do not store card numbers.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrivacyOpen(false)} color='primary'>Close</Button>
        </DialogActions>
      </Dialog>

      {error && (
        <div style={{
          color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca',
          padding: '10px', borderRadius: 8, marginBottom: 12,
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Field>
          <TextField
            fullWidth label='Full name' variant='outlined' size='small'
            value={billingDetails.name}
            onChange={(e) => setBillingDetails({ ...billingDetails, name: e.target.value })}
            required
          />
        </Field>

        <Field>
          <TextField
            fullWidth label='Email' type='email' variant='outlined' size='small'
            value={billingDetails.email}
            onChange={(e) => setBillingDetails({ ...billingDetails, email: e.target.value })}
            required
          />
        </Field>

        <div style={{ marginTop: 12 }}>
          <FormControlLabel
            control={<Checkbox checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} color='primary' />}
            label={
              <Typography variant='body2'>
                I agree to the{' '}
                <Link href='#' onClick={(e) => { e.preventDefault(); setTermsOpen(true); }}>Terms &amp; Conditions</Link>
                {' '}and{' '}
                <Link href='#' onClick={(e) => { e.preventDefault(); setPrivacyOpen(true); }}>Privacy Policy</Link>.
              </Typography>
            }
          />
        </div>

        <Row>
          <Typography variant='body2' style={{ fontWeight: 600 }}>Total</Typography>
          <Typography variant='body2' style={{ fontWeight: 600 }}>{currencySymbol}{price.toFixed(2)}</Typography>
        </Row>

        <SubmitBtn type='submit' disabled={processing || !acceptedTerms}>
          {processing ? 'Redirecting to payment…' : `Continue to Crypto Payment`}
        </SubmitBtn>
        <Note>
          You will be redirected to a secure payment page where you can pay with Bitcoin, USDT, Ethereum, and 150+ cryptocurrencies.
        </Note>
      </form>
    </>
  );
};

export default NowPaymentsCheckout;
